import type { ProviderKind } from '@pipi/shared';
import { Delete, Mic2, Search, SlidersHorizontal, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { EmptyState } from '../components/empty-state';
import { SongGrid } from '../components/song-grid';
import { TvButton } from '../components/tv-button';
import { useCatalog } from '../hooks/use-catalog';

const providerFilters: Array<{ key: ProviderKind | 'all'; label: string }> = [
  { key: 'all', label: '全部来源' },
  { key: 'demo', label: '皮皮练唱' },
  { key: 'local', label: '本地' },
  { key: 'jellyfin', label: 'Jellyfin' },
  { key: 'plex', label: 'Plex' },
  { key: 'mugen', label: 'Mugen' },
  { key: 'allkaraoke', label: 'AllKaraoke' },
];

const suggestions = ['中文流行', '合唱', '练唱', '经典', '日语', '动画'];

export function SearchPage() {
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState<ProviderKind | 'all'>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const catalogQuery = useMemo(() => ({ q: query, provider, limit: 100 }), [query, provider]);
  const { page, loading } = useCatalog(catalogQuery, 220);

  return (
    <div className="search-page">
      <section className="search-command">
        <Search size={30} />
        <input
          ref={inputRef}
          data-tv-focusable
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="输入歌名、歌手或语言"
          aria-label="搜索歌曲"
        />
        {query && <button type="button" data-tv-focusable className="clear-search" onClick={() => setQuery('')}><X size={24} />清空</button>}
        <span className="search-hint"><Mic2 size={18} />支持电视语音输入</span>
      </section>
      <section className="filter-row" aria-label="曲库来源">
        <span><SlidersHorizontal size={19} />来源</span>
        {providerFilters.map((filter) => (
          <button
            type="button"
            data-tv-focusable
            key={filter.key}
            className={provider === filter.key ? 'filter-chip is-active' : 'filter-chip'}
            onClick={() => setProvider(filter.key)}
          >{filter.label}</button>
        ))}
      </section>

      {!query && (
        <section className="search-suggestions">
          <span className="eyebrow">大家常搜</span>
          <div>{suggestions.map((suggestion) => <button type="button" data-tv-focusable key={suggestion} onClick={() => setQuery(suggestion)}>{suggestion}</button>)}</div>
        </section>
      )}

      <header className="results-heading">
        <div><span className="eyebrow">SEARCH RESULTS</span><h2>{query ? `“${query}” 的结果` : '浏览全部歌曲'}</h2></div>
        <strong>{loading ? '搜索中…' : `${page?.total.toLocaleString() ?? 0} 首`}</strong>
      </header>

      {!loading && page?.items.length === 0 ? (
        <EmptyState
          title="没有找到这首歌"
          description="换一个歌名或歌手试试，也可以到设置里同步更多曲库。"
          action={<TvButton tone="primary" onClick={() => { setQuery(''); inputRef.current?.focus(); }}><Delete size={21} />清空搜索</TvButton>}
        />
      ) : (
        <SongGrid songs={page?.items ?? []} loading={loading} />
      )}
    </div>
  );
}
